import { useState, useEffect, useRef } from 'react';
import { subscribeOpenPlaySessions, previousDateStr, cancelOpenPlaySession } from '../utils/openPlayUtil';
import { useAuthStatus } from '../context/AuthContext';
import { spinnerRing } from '../utils/bookingUtils';
import { OPEN_PLAY_CANCEL_CONFIRM_MESSAGE, OPEN_PLAY_CANCEL_EMPTY_MESSAGE } from '../constants/bookingConst';
import OpenPlaySessionCard from './OpenPlaySessionCard';
import Modal from './Modal';
import '../css/OpenPlaySessionsSection.css';

/**
 * Today's date in PH time as "yyyy-MM-dd", for the upcoming-only filter
 * below. Read straight from Intl in the Asia/Manila zone rather than going
 * through getPhToday()+formatInPhTime() — that pair round-trips a Date
 * through a local-midnight parse and then a manual UTC+8 shift, which is
 * correct but fragile, and this comparison only ever needs the plain date
 * string. Sessions are stored with a "yyyy-MM-dd" date, so a lexicographic
 * >= is a valid date comparison here.
 */
const phTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

/**
 * "Hosted open play sessions" (admin) / "Open Play sessions" (guest) — every
 * upcoming (today or later, status 'active') Open Play session, optionally
 * scoped to one sport. Both variants are a horizontally scrollable carousel
 * with prev/next arrows; they differ only in heading text, per-card content
 * (see OpenPlaySessionCard.jsx), and what sits under the rail — a
 * confirmed/awaiting legend for admin, a swipe hint for guests.
 * Live-updates via subscribeOpenPlaySessions, same as the rest of the app's
 * Firebase-backed lists.
 * @param {string|null} sportId - only show this sport's sessions; null/omitted shows every sport's
 * @param {'admin'|'guest'} variant
 * @param {(session: object) => void} onCardClick
 * @param {boolean} showEmptyState - render an empty-state message instead of nothing when there
 *   are no upcoming sessions. Admin keeps this on (confirms the feature is there even before the
 *   first session is hosted); guest leaves it off so the section stays invisible until there's
 *   actually something to show.
 *
 * Cancelling a session lives here rather than in either caller: this component
 * already owns the list and its live subscription, so a cancelled session
 * drops off the rail on its own, and neither the admin grid nor the guest
 * screen has to grow a dialog it would otherwise have no use for.
 */
function OpenPlaySessionsSection({ sportId = null, variant = 'admin', onCardClick, showEmptyState = false }) {
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  // { session, joined } while a cancellation is awaiting confirmation.
  const [pendingCancel, setPendingCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [outcome, setOutcome] = useState(null);

  // The guest booking grid can render before the visitor's sign-in finishes
  // (from the config snapshot), and the rules deny this read until then.
  const { ready: authReady } = useAuthStatus();

  useEffect(() => {
    if (!authReady) return undefined;
    // Bounded to today onwards server-side, so this screen stops downloading
    // every session ever hosted just to filter them out below. The client
    // filter stays: it also handles status/sport, and a tab left open past
    // midnight (the bound is fixed at mount).
    // Bounded to YESTERDAY, not today: an overnight session is stored under
    // the date it started, so one that began last night and is still running
    // this morning would be cut off server-side by a bound of today.
    const unsubscribe = subscribeOpenPlaySessions(setSessions, { fromDate: previousDateStr(phTodayStr()) });
    return () => unsubscribe();
  }, [authReady]);

  const todayStr = phTodayStr();
  const upcoming = sessions
    // `endDate >= today` keeps an overnight session on the rail through the
    // morning it runs into; without it, a session that started last night
    // disappears from the guest's view at midnight while it is still on.
    .filter((s) => s.status === 'active' && typeof s.date === 'string'
      && (s.date >= todayStr || (typeof s.endDate === 'string' && s.endDate >= todayStr))
      && (!sportId || s.sportId === sportId))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  // The dialogs are rendered by this component, so it has to stay mounted
  // while one is open — otherwise cancelling the LAST session on a rail that
  // hides itself when empty would unmount the success dialog before the admin
  // ever saw it.
  if (upcoming.length === 0 && !showEmptyState && !pendingCancel && !outcome) return null;

  const scrollByCard = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  /**
   * Admin hit the cancel control on a card.
   * @param {object} session
   * @param {{total: number}} breakdown - the card's live player counts
   */
  const handleCancelRequest = (session, breakdown) => {
    setPendingCancel({ session, joined: breakdown?.total || 0 });
  };

  /** Runs the cancellation the dialog is asking about. */
  const handleCancelConfirm = async () => {
    // The shared Modal has no disabled state for its confirm button, so a
    // second click during the write would fire this again — guard rather than
    // teach every other dialog in the app about loading states.
    if (cancelling || !pendingCancel) return;
    setCancelling(true);
    try {
      const { players } = await cancelOpenPlaySession(pendingCancel.session);
      setPendingCancel(null);
      setOutcome({
        title: 'Open Play cancelled',
        type: 'success',
        message: players > 0
          ? `The session is off and its courts are bookable again. ${players} player${players === 1 ? '' : 's'} were taken off it.`
          : 'The session is off and its courts are bookable again.',
      });
    } catch (error) {
      // The dialog stays open on failure: nothing was written, so the admin
      // can simply try again.
      setOutcome({
        title: 'Error',
        type: 'error',
        message: `Could not cancel this Open Play: ${error.message}`,
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <section className={`opsc-section opsc-section-${variant}`}>
      <div className="opsc-section-head">
        <div>
          <h2>{variant === 'guest' ? 'Open Play sessions' : 'Hosted open play sessions'}</h2>
          {variant === 'guest' && (
            <p className="opsc-section-sub">Pay per player and join a rotating session &mdash; no need to book a whole court.</p>
          )}
        </div>
        <div className="opsc-head-right">
          {variant === 'admin' && <span className="opsc-section-count">{upcoming.length} upcoming</span>}
          {upcoming.length > 0 && (
            <div className="opsc-carousel-arrows">
              <button type="button" className="opsc-arrow" aria-label="Scroll left" onClick={() => scrollByCard(-1)}>&lsaquo;</button>
              <button type="button" className="opsc-arrow" aria-label="Scroll right" onClick={() => scrollByCard(1)}>&rsaquo;</button>
            </div>
          )}
        </div>
      </div>

      {upcoming.length === 0 ? (
        <p className="opsc-empty">No Open Play sessions hosted yet.</p>
      ) : (
        <>
          <div className="opsc-carousel" ref={scrollRef}>
            {upcoming.map((session) => (
              <div className="opsc-carousel-item" key={session.id}>
                <OpenPlaySessionCard
                  session={session}
                  variant={variant}
                  onClick={onCardClick}
                  onDelete={variant === 'admin' ? handleCancelRequest : null}
                />
              </div>
            ))}
          </div>
          {variant === 'guest' ? (
            <p className="opsc-swipe-hint">&harr; Swipe for more sessions &middot; {upcoming.length} scheduled</p>
          ) : (
            <div className="opsc-legend">
              <span><span className="opsc-sw opsc-sw-confirmed"></span>confirmed</span>
              <span><span className="opsc-sw opsc-sw-awaiting"></span>awaiting your approval</span>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={!!pendingCancel}
        title="Cancel Open Play"
        message={pendingCancel?.joined > 0 ? OPEN_PLAY_CANCEL_CONFIRM_MESSAGE : OPEN_PLAY_CANCEL_EMPTY_MESSAGE}
        type="warning"
        // The dialog's own words carry the warning; the ⚠️ beside them just
        // repeats it. `type` still drives the confirm button's colour.
        hideIcon
        confirmText={cancelling ? 'Cancelling…' : 'Yes, cancel it'}
        showCancel
        cancelText="Keep it"
        onConfirm={handleCancelConfirm}
        // Ignored mid-write: closing the dialog under an in-flight cancel
        // would leave the admin with no idea whether it went through.
        onClose={() => { if (!cancelling) setPendingCancel(null); }}
      >
        {cancelling && <div className="opsc-cancelling">{spinnerRing('Cancelling session…')}</div>}
      </Modal>

      <Modal
        isOpen={!!outcome}
        title={outcome?.title || ''}
        message={outcome?.message || ''}
        type={outcome?.type || 'info'}
        confirmText="OK"
        onClose={() => setOutcome(null)}
      />
    </section>
  );
}

export default OpenPlaySessionsSection;
